import Checkbox from '@jetbrains/ring-ui/components/checkbox/checkbox'
import classNames from 'classnames'
import * as React from 'react'
import type {ChangeEventHandler, MouseEventHandler} from 'react'

import {walkItemAncestors, walkItemAndDescendants} from '../../../../AgentPoolPage.selectors'
import type {
  AgentPoolProjectsTreeProjectItem,
  ToggleExpandHandler,
} from '../../../../AgentPoolPage.types'
import {
  EXPAND_HANDLE_WIDTH,
  ExpandHandle,
  IndentedContainer,
} from '../../../../IndentedExpandHandle/IndentedExpandHandle'
import ProjectPath, {ProjectLine} from '../../../../ProjectPath/ProjectPath'

import styles from './ProjectsTreeItem.css'

type SelectHandler = (selected: AgentPoolProjectsTreeProjectItem) => void

type LinearExpandableAgentPoolProjectProps = {
  item: AgentPoolProjectsTreeProjectItem
  agentPoolId: string
  searchQuery: string
  onToggleExpand: ToggleExpandHandler
  selected: boolean | undefined
  onSelect: SelectHandler
  disabled?: boolean
  disabledHint?: string | null
  className?: string
}

export default function ProjectsTreeItem({
  item,
  className,
  agentPoolId,
  searchQuery,
  onToggleExpand,
  onSelect,
  selected = false,
  disabled = false,
  disabledHint,
}: LinearExpandableAgentPoolProjectProps) {
  const {rawId, agentPools, level, expandable, expanded} = item

  let collapsedParentsNumber = 0
  const adjacentCollapsedProjects: Array<AgentPoolProjectsTreeProjectItem> = []
  let reachedCollapsedParent = false

  walkItemAncestors(item.parent, parent => {
    if (!parent.assigned && !parent.archived) {
      reachedCollapsedParent = true
    } else {
      collapsedParentsNumber += 1
      if (!reachedCollapsedParent) {
        adjacentCollapsedProjects.push(parent)
      }
    }
  })

  const handleToggleExpand = React.useCallback(() => {
    onToggleExpand(rawId, !expanded)
  }, [expanded, onToggleExpand, rawId])
  const handleCheckboxClick: MouseEventHandler = React.useCallback(e => {
    e.stopPropagation()
  }, [])
  const handleCheckboxChange: ChangeEventHandler = React.useCallback(() => {
    onSelect(item)
  }, [item, onSelect])

  const otherPools = agentPools.filter(pool => pool.id !== agentPoolId).map(pool => pool.name)
  const otherPoolsLine = otherPools.length > 0 ? `also in ${otherPools.join(',')}` : undefined

  let currentlyExpandable = false

  if (expandable) {
    walkItemAndDescendants(item, curentItem => {
      currentlyExpandable = currentlyExpandable || (!curentItem.assigned && !curentItem.archived)
    })
  } else {
    currentlyExpandable = false
  }

  return (
    <div
      className={classNames(className, styles.project, {
        [styles.expandableProject]: currentlyExpandable,
      })}
      role="gridcell"
      tabIndex={-1}
      onClick={currentlyExpandable ? handleToggleExpand : undefined}
      onKeyDown={currentlyExpandable ? handleToggleExpand : undefined}
    >
      <IndentedContainer
        level={level - collapsedParentsNumber}
        className={styles.indentedContainer}
      >
        {currentlyExpandable ? (
          <ExpandHandle
            expanded={expanded}
            expandable={currentlyExpandable}
            className={styles.chevron}
            iconClassName={styles.chevronIcon}
          />
        ) : (
          <div style={{marginLeft: EXPAND_HANDLE_WIDTH}} />
        )}

        <Checkbox
          onClick={handleCheckboxClick}
          disabled={disabled}
          onChange={handleCheckboxChange}
          checked={selected}
          containerClassName={styles.checkboxContainer}
          cellClassName={styles.checkboxCell}
          labelClassName={styles.checkboxLabel}
          {...(disabledHint ? {title: disabledHint} : {})}
        >
          <ProjectPath
            onClick={handleCheckboxClick}
            projectItem={item}
            parentItems={adjacentCollapsedProjects}
            searchQuery={searchQuery}
            itemComponent={ProjectLine}
            itemContainerClassName={styles.pathItem}
          />
        </Checkbox>
        <div className={styles.projectAgentPools} title={otherPoolsLine}>
          {otherPoolsLine}
        </div>
      </IndentedContainer>
    </div>
  )
}
